import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { doc, getDoc, collection, query, where, getDocs, updateDoc } from 'firebase/firestore';
import { db } from '../lib/firebase';
import { useStore } from '../store';
import type { Feedback, Game, Profile, Goal, Drill, DrillDrawingPoint } from '../types';
import { MessageSquare, Dumbbell, Link as LinkIcon, Target, FileDown, Pencil, Save, X, Plus, ChevronDown, ChevronUp, ArrowLeft } from 'lucide-react';
import { PDFDownloadLink } from '@react-pdf/renderer';
import GoalAnalytics from '../components/GoalAnalytics';
import GoalDiagram from '../components/GoalDiagram';
import ShotPositionDiagram from '../components/ShotPositionDiagram';
import ShotPositionAnalytics from '../components/ShotPositionAnalytics';
import GamePDF from '../components/GamePDF';
import { BarChart, ResponsiveContainer, XAxis, YAxis, Tooltip, Bar } from 'recharts';
import DrillDrawingCanvas from '../components/DrillDrawingCanvas';
import { useTranslation } from '../i18n/useTranslation';

const formatLabel = (label: string) => {
  return label
    .replace(/ Goal$/, '')
    .replace(/ Shot$/, '')
    .replace(' / ', '/');
};

const countGoalTypes = (goals: Goal[], type: 'shotType' | 'gameSituation') => {
  const counts: Record<string, number> = {};
  goals.forEach(goal => {
    const value = goal[type];
    if (value) {
      counts[value] = (counts[value] || 0) + 1;
    }
  });
  return Object.entries(counts)
    .map(([name, value]) => ({ 
      name: formatLabel(name),
      fullName: name,
      value 
    }))
    .sort((a, b) => b.value - a.value);
};

export default function FeedbackDetails() {
  const { t } = useTranslation();
  const { id } = useParams();
  const user = useStore((state) => state.user);
  const [feedback, setFeedback] = useState<Feedback | null>(null);
  const [game, setGame] = useState<Game | null>(null);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [goals, setGoals] = useState<Goal[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [isEditing, setIsEditing] = useState(false);
  const [editedFeedback, setEditedFeedback] = useState<{
    content: string;
    drills: Drill[];
  }>({ content: '', drills: [] });
  const [newDrill, setNewDrill] = useState<Drill>({
    title: '',
    description: '',
    videoUrl: '',
    exerciseDrawing: []
  });
  const [showNewDrillForm, setShowNewDrillForm] = useState(false);
  const [showDrawingCanvas, setShowDrawingCanvas] = useState(false);
  const [expandedDrills, setExpandedDrills] = useState<Record<string, boolean>>({});

  useEffect(() => {
    const fetchFeedbackDetails = async () => {
      if (!id) return;

      try {
        // Fetch feedback
        const feedbackDoc = await getDoc(doc(db, 'feedback', id));
        if (!feedbackDoc.exists()) {
          setError(t('feedbackDetails.feedbackNotFound'));
          setLoading(false);
          return;
        }

        const feedbackData = {
          id: feedbackDoc.id,
          ...feedbackDoc.data(),
          createdAt: feedbackDoc.data().createdAt.toDate()
        } as Feedback;
        setFeedback(feedbackData);

        // Fetch associated game
        const gameDoc = await getDoc(doc(db, 'games', feedbackData.gameId));
        if (gameDoc.exists()) {
          const gameData = {
            id: gameDoc.id,
            ...gameDoc.data(),
            date: new Date(gameDoc.data().date),
            createdAt: new Date(gameDoc.data().createdAt)
          } as Game;
          setGame(gameData);

          // Fetch goals for the game
          const goalsQuery = query(
            collection(db, 'goals'),
            where('gameId', '==', gameData.id)
          );
          const goalsSnapshot = await getDocs(goalsQuery);
          const goalsData = goalsSnapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data(),
            createdAt: new Date(doc.data().createdAt)
          })) as Goal[];
          setGoals(goalsData);

          // Fetch profile of game owner
          const profileDoc = await getDoc(doc(db, 'profiles', gameDoc.data().userId));
          if (profileDoc.exists()) {
            setProfile({
              id: profileDoc.id,
              ...profileDoc.data(),
              createdAt: profileDoc.data().createdAt.toDate()
            } as Profile);
          }
        }

      } catch (err) {
        console.error('Error fetching feedback details:', err);
        setError(t('feedbackDetails.failedToLoadFeedbackDetails'));
      } finally {
        setLoading(false);
      }
    };

    fetchFeedbackDetails();
  }, [id]);

  const handleSaveFeedback = async () => {
    if (!feedback || !user) return;

    try {
      await updateDoc(doc(db, 'feedback', feedback.id), {
        content: editedFeedback.content,
        drills: editedFeedback.drills,
      });

      setFeedback({
        ...feedback,
        content: editedFeedback.content,
        drills: editedFeedback.drills,
      });
      setIsEditing(false);
      setShowNewDrillForm(false);
    } catch (err) {
      console.error('Error updating feedback:', err);
      setError(t('feedbackDetails.failedToUpdateFeedback'));
    }
  };

  const handleAddDrill = () => {
    if (!newDrill.title || !newDrill.description) return;

    setEditedFeedback(prev => ({
      ...prev,
      drills: [...prev.drills, newDrill]
    }));
    setNewDrill({
      title: '',
      description: '',
      videoUrl: '',
      exerciseDrawing: []
    });
    setShowNewDrillForm(false);
    setShowDrawingCanvas(false);
  };

  const handleRemoveDrill = (index: number) => {
    setEditedFeedback(prev => ({
      ...prev,
      drills: prev.drills.filter((_, i) => i !== index)
    }));
  };

  const handleDrawingChange = (points: DrillDrawingPoint[]) => {
    setNewDrill(prev => ({
      ...prev,
      exerciseDrawing: points
    }));
  };

  const toggleDrillDrawing = (drillIndex: number) => {
    const key = `drill-${drillIndex}`;
    setExpandedDrills(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  useEffect(() => {
    if (feedback) {
      setEditedFeedback({
        content: feedback.content,
        drills: feedback.drills || [],
      });
    }
  }, [feedback]);

  const getPdfFilename = () => {
    if (!game) return 'game-report.pdf';
    const timestamp = game.date.toISOString().split('T')[0];
    const opponent = game.opponent.toLowerCase().replace(/[^a-z0-9]/g, '-');
    return `game-report-${opponent}-${timestamp}.pdf`;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#1b3968] mb-4"></div>
          <p className="text-gray-600">{t('feedbackDetails.loadingFeedbackDetails')}</p>
        </div>
      </div>
    );
  }

  if (error || !feedback || !game) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-8">
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
          {error || t('feedbackDetails.feedbackNotFound')}
        </div>
      </div>
    );
  }

  return (
    <div className='max-w-6xl mx-auto px-4 py-8'>
      <div className='flex flex-col mb-8'>
        <Link
          to='/feedback'
          className='inline-flex items-center text-[#1b3968] hover:text-[#1b3968]/80 mb-4 w-fit'
        >
          <ArrowLeft className='h-4 w-4 mr-1' />
          {t('feedbackDetails.backToFeedback')}
        </Link>

        <div className='flex items-center gap-2 mb-4'>
          <MessageSquare className='h-6 w-6 text-[#1b3968]' />
          <h1 className='text-2xl md:text-3xl font-bold italic'>
            {t('feedbackDetails.title')}
          </h1>
        </div>

        <div className='flex flex-wrap gap-3'>
          {feedback?.authorId === user?.uid && !isEditing && (
            <button
              onClick={() => setIsEditing(true)}
              className='inline-flex items-center gap-2 px-3 py-1.5 text-sm text-[#1b3968] hover:text-[#1b3968]/80 border border-[#1b3968] rounded-md'
            >
              <Pencil className='h-4 w-4' />
              <span>{t('feedbackDetails.edit')}</span>
            </button>
          )}
          {game && (
            <PDFDownloadLink
              document={<GamePDF game={game} goals={goals} />}
              fileName={getPdfFilename()}
              className='inline-flex items-center gap-2 px-3 py-1.5 text-sm text-[#1b3968] hover:text-[#1b3968]/80 border border-[#1b3968] rounded-md'
            >
              {({ loading }) => (
                <>
                  <FileDown className='h-4 w-4' />
                  <span>{loading ? t('feedbackDetails.preparing') : t('feedbackDetails.export')}</span>
                </>
              )}
            </PDFDownloadLink>
          )}
        </div>
      </div>

      <div className='grid grid-cols-1 lg:grid-cols-2 gap-8'>
        <div>
          {/* Feedback Information */}
          <div className='bg-white shadow rounded-lg p-6 mb-8'>
            <div className='grid grid-cols-1 md:grid-cols-2 gap-4 mb-6'>
              <div>
                <h3 className='text-sm font-medium text-gray-500'>{t('feedbackDetails.from')}</h3>
                <p className='mt-1 text-lg text-gray-900'>
                  {feedback.authorName}
                </p>
              </div>
              <div>
                <h3 className='text-sm font-medium text-gray-500'>{t('feedbackDetails.date')}</h3>
                <p className='mt-1 text-lg text-gray-900'>
                  {feedback.createdAt.toLocaleDateString()}
                </p>
              </div>
            </div>

            <div className='bg-gray-50 rounded-lg p-4'>
              <div className='flex items-center gap-2 mb-4'>
                <Target className='h-4 w-4 text-gray-400' />
                <h3 className='font-medium text-gray-900'>{t('feedbackDetails.gameDetails')}</h3>
              </div>
              <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
                <div>
                  <p className='text-sm text-gray-500'>{t('feedbackDetails.opponent')}</p>
                  <p className='text-gray-900'>{game.opponent}</p>
                </div>
                <div>
                  <p className='text-sm text-gray-500'>{t('feedbackDetails.date')}</p>
                  <p className='text-gray-900'>
                    {game.date.toLocaleDateString()}
                  </p>
                </div>
                {profile && (
                  <div className='md:col-span-2'>
                    <p className='text-sm text-gray-500'>{t('feedbackDetails.team')}</p>
                    <p className='text-gray-900'>{profile.team}</p>
                  </div>
                )}
              </div>
            </div>

            <div className='mt-6'>
              <h3 className='font-medium text-gray-900 mb-2'>{t('feedbackDetails.feedback')}</h3>
              {isEditing ? (
                <textarea
                  value={editedFeedback.content}
                  onChange={(e) =>
                    setEditedFeedback((prev) => ({
                      ...prev,
                      content: e.target.value,
                    }))
                  }
                  className='w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-[#1b3968] focus:border-[#1b3968]'
                  rows={6}
                />
              ) : (
                <p className='text-gray-700 whitespace-pre-wrap'>
                  {feedback.content}
                </p>
              )}
            </div>

            {/* Drills */}
            <div className='mt-6 pt-6 border-t border-gray-200'>
              <div className='flex items-center justify-between mb-4'>
                <div className='flex items-center gap-2'>
                  <Dumbbell className='h-4 w-4 text-[#1b3968]' />
                  <h3 className='font-medium text-[#1b3968]'>
                    {t('feedbackDetails.suggestedDrills')}
                  </h3>
                </div>
                {isEditing && !showNewDrillForm && (
                  <button
                    type='button'
                    onClick={() => setShowNewDrillForm(true)}
                    className='text-[#1b3968] hover:text-[#1b3968]/80 text-sm flex items-center gap-2'
                  >
                    <Plus className='h-4 w-4' />
                    {t('feedbackDetails.addDrill')}
                  </button>
                )}
              </div>

              <div className='space-y-4'>
                {isEditing ? (
                  <>
                    {editedFeedback.drills.map((drill, index) => (
                      <div
                        key={index}
                        className='bg-gray-50 rounded-lg p-4 relative'
                      >
                        <button
                          onClick={() => handleRemoveDrill(index)}
                          className='absolute top-2 right-2 text-gray-400 hover:text-red-500'
                        >
                          <X className='h-4 w-4' />
                        </button>
                        <h4 className='font-medium text-gray-900 mb-2'>
                          {drill.title}
                        </h4>
                        <p className='text-gray-600 mb-3'>
                          {drill.description}
                        </p>
                        {drill.videoUrl && (
                          <a
                            href={drill.videoUrl}
                            target='_blank'
                            rel='noopener noreferrer'
                            className='text-[#1b3968] hover:text-[#1b3968]/80 inline-flex items-center gap-1'
                          >
                            <LinkIcon className='h-4 w-4' />
                            {t('feedbackDetails.watchVideo')}
                          </a>
                        )}
                        {drill.exerciseDrawing &&
                          drill.exerciseDrawing.length > 0 && (
                            <div className='mt-3'>
                              <DrillDrawingCanvas
                                points={drill.exerciseDrawing}
                                readOnly={true}
                              />
                            </div>
                          )}
                      </div>
                    ))}

                    {showNewDrillForm && (
                      <div className='bg-white border border-gray-200 rounded-lg p-4 space-y-4'>
                        <input
                          type='text'
                          value={newDrill.title}
                          onChange={(e) =>
                            setNewDrill((prev) => ({
                              ...prev,
                              title: e.target.value,
                            }))
                          }
                          placeholder={t('feedbackDetails.drillTitle')}
                          className='w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-[#1b3968] focus:border-[#1b3968]'
                        />
                        <textarea
                          value={newDrill.description}
                          onChange={(e) =>
                            setNewDrill((prev) => ({
                              ...prev,
                              description: e.target.value,
                            }))
                          }
                          placeholder={t('feedbackDetails.drillDescription')}
                          className='w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-[#1b3968] focus:border-[#1b3968]'
                          rows={3}
                        />
                        <input
                          type='url'
                          value={newDrill.videoUrl}
                          onChange={(e) =>
                            setNewDrill((prev) => ({
                              ...prev,
                              videoUrl: e.target.value,
                            }))
                          }
                          placeholder={t('feedbackDetails.videoUrlOptional')}
                          className='w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-[#1b3968] focus:border-[#1b3968]'
                        />
                        <div>
                          <button
                            type='button'
                            onClick={() =>
                              setShowDrawingCanvas(!showDrawingCanvas)
                            }
                            className='text-[#1b3968] hover:text-[#1b3968]/80 text-sm flex items-center gap-1'
                          >
                            <Pencil className='h-4 w-4' />
                            {showDrawingCanvas
                              ? t('feedbackDetails.hideDrawingCanvas')
                              : t('feedbackDetails.drawExercise')}
                          </button>
                          {showDrawingCanvas && (
                            <div className='mt-4'>
                              <DrillDrawingCanvas
                                points={newDrill.exerciseDrawing}
                                onChange={handleDrawingChange}
                              />
                              <p className='mt-2 text-sm text-gray-500'>
                                {t('feedbackDetails.drawMovementPattern')}
                              </p>
                            </div>
                          )}
                        </div>
                        <div className='flex justify-end space-x-2'>
                          <button
                            type='button'
                            onClick={() => {
                              setShowNewDrillForm(false);
                              setShowDrawingCanvas(false);
                              setNewDrill({
                                title: '',
                                description: '',
                                videoUrl: '',
                                exerciseDrawing: [],
                              });
                            }}
                            className='px-3 py-1.5 text-gray-600 hover:text-gray-900'
                          >
                            {t('feedbackDetails.cancel')}
                          </button>
                          <button
                            type='button'
                            onClick={handleAddDrill}
                            className='px-3 py-1.5 bg-[#1b3968] text-white rounded-md hover:bg-[#1b3968]/90'
                            disabled={!newDrill.title || !newDrill.description}
                          >
                            {t('feedbackDetails.addDrill')}
                          </button>
                        </div>
                      </div>
                    )}
                  </>
                ) : (
                  feedback.drills?.map((drill, index) => (
                    <div key={index} className='bg-gray-50 rounded-lg p-4'>
                      <h4 className='font-medium text-gray-900 mb-2'>
                        {drill.title}
                      </h4>
                      <p className='text-gray-600 mb-3'>{drill.description}</p>
                      <div className='space-y-3'>
                        {drill.videoUrl && (
                          <a
                            href={drill.videoUrl}
                            target='_blank'
                            rel='noopener noreferrer'
                            className='text-[#1b3968] hover:text-[#1b3968]/80 inline-flex items-center gap-1'
                          >
                            <LinkIcon className='h-4 w-4' />
                            {t('feedbackDetails.watchVideo')}
                          </a>
                        )}
                        {drill.exerciseDrawing &&
                          drill.exerciseDrawing.length > 0 && (
                            <div>
                              <button
                                onClick={() => toggleDrillDrawing(index)}
                                className='px-3 py-1.5 border border-[#1b3968] text-[#1b3968] hover:bg-[#1b3968] hover:text-white rounded-md text-sm flex items-center gap-2 transition-colors'
                              >
                                <Pencil className='h-4 w-4' />
                                {t('feedbackDetails.exerciseDrawing')}
                                {expandedDrills[`drill-${index}`] ? (
                                  <ChevronUp className='h-4 w-4' />
                                ) : (
                                  <ChevronDown className='h-4 w-4' />
                                )}
                              </button>
                              {expandedDrills[`drill-${index}`] && (
                                <div className='mt-3'>
                                  <DrillDrawingCanvas
                                    points={drill.exerciseDrawing}
                                    readOnly={true}
                                  />
                                </div>
                              )}
                            </div>
                          )}
                      </div>
                    </div>
                  ))
                )}
              </div>

              {isEditing && (
                <div className='mt-6 flex justify-end space-x-3'>
                  <button
                    type='button'
                    onClick={() => {
                      setIsEditing(false);
                      setShowNewDrillForm(false);
                      setEditedFeedback({
                        content: feedback.content,
                        drills: feedback.drills || [],
                      });
                    }}
                    className='px-4 py-2 text-gray-600 hover:text-gray-900'
                  >
                    {t('feedbackDetails.cancel')}
                  </button>
                  <button
                    type='button'
                    onClick={handleSaveFeedback}
                    className='px-4 py-2 bg-[#1b3968] text-white rounded-md hover:bg-[#1b3968]/90 flex items-center gap-2'
                  >
                    <Save className='h-4 w-4' />
                    {t('feedbackDetails.saveChanges')}
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Game Statistics */}
          <div className='bg-white shadow rounded-lg p-6'>
            <h2 className='text-xl font-bold mb-6'>{t('feedbackDetails.gameStatistics')}</h2>
            <div className='grid grid-cols-1 md:grid-cols-4 gap-4'>
              <div className='bg-gray-50 p-4 rounded-lg'>
                <h3 className='text-sm font-medium text-gray-500'>
                  {t('feedbackDetails.shotsFaced')}
                </h3>
                <p className='text-2xl font-bold text-[#1b3968]'>
                  {game.shotsFaced || '—'}
                </p>
              </div>
              <div className='bg-gray-50 p-4 rounded-lg'>
                <h3 className='text-sm font-medium text-gray-500'>{t('feedbackDetails.saves')}</h3>
                <p className='text-2xl font-bold text-green-600'>
                  {game.saves || '—'}
                </p>
              </div>
              <div className='bg-gray-50 p-4 rounded-lg'>
                <h3 className='text-sm font-medium text-gray-500'>
                  {t('feedbackDetails.goalsAgainst')}
                </h3>
                <p className='text-2xl font-bold text-red-600'>
                  {goals.length}
                </p>
              </div>
              <div className='bg-gray-50 p-4 rounded-lg'>
                <h3 className='text-sm font-medium text-gray-500'>{t('feedbackDetails.savePercentage')}</h3>
                <p className='text-2xl font-bold text-[#1b3968]'>
                  {game.shotsFaced && game.saves
                    ? `${((game.saves / game.shotsFaced) * 100).toFixed(1)}%`
                    : '—'}
                </p>
              </div>
            </div>
          </div>
        </div>

        <div>
          {/* Goal Analysis */}
          <div className='bg-white shadow rounded-lg p-6 mb-8'>
            <h2 className='text-xl font-bold mb-6'>{t('feedbackDetails.goalDistribution')}</h2>
            <GoalAnalytics
              goals={goals}
              width={Math.min(window.innerWidth - 40, 500)}
              height={Math.min(window.innerWidth - 40, 375)}
            />
          </div>

          <div className='bg-white shadow rounded-lg p-6 mb-8'>
            <h2 className='text-xl font-bold mb-6'>{t('feedbackDetails.goalLocations')}</h2>
            <GoalDiagram
              onPositionSelect={() => {}}
              goals={goals}
              width={Math.min(window.innerWidth - 40, 500)}
              height={Math.min(window.innerWidth - 40, 375)}
              readOnly={true}
            />
          </div>

          <div className='bg-white shadow rounded-lg p-6 mb-8'>
            <h2 className='text-xl font-bold mb-6'>{t('feedbackDetails.shotDistribution')}</h2>
            <ShotPositionAnalytics
              goals={goals}
              width={Math.min(window.innerWidth - 40, 500)}
              height={Math.min(window.innerWidth - 40, 375)}
            />
          </div>

          <div className='bg-white shadow rounded-lg p-6 mb-8'>
            <h2 className='text-xl font-bold mb-6'>{t('feedbackDetails.shotLocations')}</h2>
            <ShotPositionDiagram
              onPositionSelect={() => {}}
              goals={goals}
              width={Math.min(window.innerWidth - 40, 500)}
              height={Math.min(window.innerWidth - 40, 375)}
              readOnly={true}
            />
          </div>

          {/* Shot Types and Game Situations */}
          <div className='grid grid-cols-1 gap-8'>
            <div className='bg-white shadow rounded-lg p-6'>
              <h3 className='text-lg font-medium mb-4'>
                {t('feedbackDetails.shotTypesDistribution')}
              </h3>
              <div className='h-[300px]'>
                <ResponsiveContainer width='100%' height='100%'>
                  <BarChart
                    data={countGoalTypes(goals, 'shotType')}
                    layout='vertical'
                    margin={{ top: 5, right: 30, left: 80, bottom: 5 }}
                  >
                    <defs>
                      <linearGradient
                        id='feedbackShotTypeGradient'
                        x1='0'
                        y1='0'
                        x2='1'
                        y2='0'
                      >
                        <stop offset='0%' stopColor='#3B82F6' />
                        <stop offset='50%' stopColor='#1D4ED8' />
                        <stop offset='100%' stopColor='#1E40AF' />
                      </linearGradient>
                    </defs>
                    <XAxis
                      type='number'
                      tickCount={
                        Math.max(
                          ...countGoalTypes(goals, 'shotType').map(
                            (item) => item.value
                          )
                        ) + 1
                      }
                      domain={[0, 'dataMax']}
                      allowDecimals={false}
                      tick={{ fontSize: 12, fill: '#64748B', fontWeight: 500 }}
                      axisLine={{ stroke: '#E2E8F0', strokeWidth: 2 }}
                      tickLine={{ stroke: '#CBD5E1' }}
                    />
                    <YAxis
                      type='category'
                      dataKey='name'
                      width={80}
                      tick={{ fontSize: 12, fill: '#475569', fontWeight: 500 }}
                      axisLine={{ stroke: '#E2E8F0', strokeWidth: 2 }}
                      tickLine={false}
                    />
                    <Tooltip
                      contentStyle={{
                        backgroundColor: 'rgba(255, 255, 255, 0.95)',
                        backdropFilter: 'blur(12px)',
                        border: '1px solid rgba(255, 255, 255, 0.2)',
                        borderRadius: '12px',
                        boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
                        fontSize: '14px',
                        fontWeight: '500',
                      }}
                      formatter={(value: number, name: string, props: any) => [
                        value,
                        props.payload.fullName,
                      ]}
                    />
                    <Bar
                      dataKey='value'
                      fill='url(#feedbackShotTypeGradient)'
                      radius={[0, 6, 6, 0]}
                      stroke='rgba(255, 255, 255, 0.5)'
                      strokeWidth={1}
                    />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>

            <div className='bg-white shadow rounded-lg p-6'>
              <h3 className='text-lg font-medium mb-4'>
                {t('feedbackDetails.gameSituationsDistribution')}
              </h3>
              <div className='h-[300px]'>
                <ResponsiveContainer width='100%' height='100%'>
                  <BarChart
                    data={countGoalTypes(goals, 'gameSituation')}
                    layout='vertical'
                    margin={{ top: 5, right: 30, left: 80, bottom: 5 }}
                  >
                    <defs>
                      <linearGradient
                        id='feedbackGameSituationGradient'
                        x1='0'
                        y1='0'
                        x2='1'
                        y2='0'
                      >
                        <stop offset='0%' stopColor='#10B981' />
                        <stop offset='50%' stopColor='#059669' />
                        <stop offset='100%' stopColor='#047857' />
                      </linearGradient>
                    </defs>
                    <XAxis
                      type='number'
                      tickCount={
                        Math.max(
                          ...countGoalTypes(goals, 'gameSituation').map(
                            (item) => item.value
                          )
                        ) + 1
                      }
                      domain={[0, 'dataMax']}
                      allowDecimals={false}
                      tick={{ fontSize: 12, fill: '#64748B', fontWeight: 500 }}
                      axisLine={{ stroke: '#E2E8F0', strokeWidth: 2 }}
                      tickLine={{ stroke: '#CBD5E1' }}
                    />
                    <YAxis
                      type='category'
                      dataKey='name'
                      width={80}
                      tick={{ fontSize: 12, fill: '#475569', fontWeight: 500 }}
                      axisLine={{ stroke: '#E2E8F0', strokeWidth: 2 }}
                      tickLine={false}
                    />
                    <Tooltip
                      contentStyle={{
                        backgroundColor: 'rgba(255, 255, 255, 0.95)',
                        backdropFilter: 'blur(12px)',
                        border: '1px solid rgba(255, 255, 255, 0.2)',
                        borderRadius: '12px',
                        boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
                        fontSize: '14px',
                        fontWeight: '500',
                      }}
                      formatter={(value: number, name: string, props: any) => [
                        value,
                        props.payload.fullName,
                      ]}
                    />
                    <Bar
                      dataKey='value'
                      fill='url(#feedbackGameSituationGradient)'
                      radius={[0, 6, 6, 0]}
                      stroke='rgba(255, 255, 255, 0.5)'
                      strokeWidth={1}
                    />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}